'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useId, useRef, useState } from 'react';
import { useOrderTransition } from '@/components/transition/OrderTransition';
import { Icon } from '@/components/ui/Icon';
import type { NavLink } from '@/data/resolve';
import { cn } from '@/lib/cn';

type MobileMenuProps = {
  items: readonly NavLink[];
  phone: { value: string; href: string };
  cta: { label: string; href: string };
  /** Accessible names — the panel has no visible heading. */
  labels: { open: string; close: string; panel: string; nav: string };
};

export function MobileMenu({ items, phone, cta, labels }: MobileMenuProps) {
  const pathname = usePathname();
  const startOrder = useOrderTransition();
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /**
   * The panel remembers *which* route it was opened on, so navigating
   * closes it during render — no effect, no cascading re-render.
   */
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const open = openedOn === pathname;
  const setOpen = (next: boolean) => setOpenedOn(next ? pathname : null);

  // Lock scroll, close on Escape, keep focus inside the panel.
  useEffect(() => {
    if (!open) return;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpenedOn(null);
        triggerRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      body.style.overflow = previousOverflow;
      body.style.paddingRight = '';
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? labels.close : labels.open}
        className={cn(
          'grid size-11 place-items-center rounded-xl border border-line bg-white',
          'text-ink transition-colors hover:bg-surface nav:hidden',
        )}
      >
        <Icon name={open ? 'close' : 'menu'} size={22} strokeWidth={2} />
      </button>

      {/* Backdrop */}
      <div
        hidden={!open}
        onClick={() => setOpen(false)}
        className="fixed inset-0 top-(--spacing-header) z-40 bg-navy-950/45 backdrop-blur-[2px] nav:hidden"
      />

      <div
        ref={panelRef}
        id={panelId}
        hidden={!open}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={labels.panel}
        className={cn(
          'fixed inset-x-0 top-(--spacing-header) z-50 max-h-[calc(100dvh-var(--spacing-header))]',
          'overflow-y-auto overscroll-contain border-b border-line bg-white',
          'px-5 pt-4 pb-8 shadow-[0_24px_48px_-24px_rgb(13_27_46/0.35)] outline-none nav:hidden',
        )}
      >
        <nav aria-label={labels.nav}>
          <ul className="flex flex-col gap-1">
            {items.map((item) => {
              const active =
                pathname === item.href ||
                pathname.startsWith(`${item.href}/`);
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'block rounded-xl px-4 py-3.5 text-base font-semibold transition-colors',
                      active
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-ink hover:bg-surface',
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="mt-5 flex flex-col gap-3 border-t border-line pt-5">
          <a
            href={phone.href}
            className="inline-flex items-center gap-2.5 text-base font-bold text-ink"
          >
            <Icon name="phone" size={18} className="text-brand-600" />
            {phone.value}
          </a>

          <Link
            href={cta.href}
            onClick={(event) => {
              // Plain taps get the curtain transition; modified clicks keep
              // native link behaviour.
              if (
                !startOrder ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              ) {
                return;
              }
              event.preventDefault();
              setOpen(false);
              startOrder();
            }}
            className={cn(
              'inline-flex h-13 items-center justify-center gap-2 rounded-full',
              'bg-brand-600 px-6 text-[0.9375rem] font-bold text-white',
              'transition-colors hover:bg-brand-700',
            )}
          >
            {cta.label}
            <Icon name="arrowRight" size={18} />
          </Link>
        </div>
      </div>
    </>
  );
}
